import PropTypes from 'prop-types';
import { format } from 'date-fns';

// Design System
import { Message } from 'jl-design-system/components/message/Message';
import { UnderlinedButton } from 'jl-design-system/elements/button/Button';

// constant
import deliveryConstants from '../../constants/deliveryConstants';

// Components
import Container from '../container';

export const getUndeliverableErrorMessage = ({
  items,
  editBasket,
  onChangeAddressClick,
  undeliverableItemsInBasket,
}) => {
  const standardDatePattern = 'DD MMMM YYYY';

  const getEditBasketOrRemoveItemLink = (plural = '') => (
    <>
      ,{' '}or{' '}
      <UnderlinedButton onClick={editBasket}>
        edit your basket
      </UnderlinedButton>{' '}and remove the item{plural}
    </>
  );

  const messageNextStepsText = (undeliverableItems = 0) => {
    const plural = undeliverableItems > 1 ? 's.' : '.';
    return (
      <p>
        Please {' '}
        <UnderlinedButton onClick={onChangeAddressClick}>
          change your delivery address
        </UnderlinedButton> to continue
        {getEditBasketOrRemoveItemLink(plural)}
      </p>
    );
  };

  const undeliverableItemsBody = (
    undeliverableItems = undefined,
    itemsCount = 0,
  ) => (
    <>
      {undeliverableItems &&
        <ul>{undeliverableItems}</ul>}
      {messageNextStepsText(itemsCount)}
    </>
  );

  const multipleUndeliverableItemsBody = (
    undeliverableItems = undefined,
    itemsCount = false,
    date = undefined,
  ) => {
    const itemText = itemsCount > 1 ? 'These items are' : 'This item is';

    return (
      <>
        {date &&
          <>{itemText} not available until <strong>{date}</strong>:</>}
        {undeliverableItems &&
          <ul>{undeliverableItems}</ul>}
      </>
    );
  };

  const getUndeliverableItems = undeliverableItems => undeliverableItems.map(ud => (
    <li key={ud.id} className="mb-0">{ud.name}</li>
  ));

  const getRestrictedPreOrderItemsErrorMessage = (restrictedPreorderItemsList = []) => {
    const groupedByStreetDate = restrictedPreorderItemsList.reduce((acc, item) => {
      if (!acc[item.streetDate]) {
        acc[item.streetDate] = [];
      }
      acc[item.streetDate].push(item);
      return acc;
    }, {});

    const streetDates = Object.keys(groupedByStreetDate);
    const undeliverableItems = getUndeliverableItems(restrictedPreorderItemsList);
    const undeliverableItemsCount = restrictedPreorderItemsList.length;

    if (streetDates.length === 1) {
      const streetDate = streetDates[0];
      const date = streetDate ? ` until ${format(new Date(streetDate), standardDatePattern)}` : '';
      const isMultipleRestrictedPreOrderItem = undeliverableItemsCount === 1;
      const titleText = !isMultipleRestrictedPreOrderItem ? 'These items' : 'This item';

      return {
        title: `${titleText} cannot be ordered to your delivery address${date}.`,
        body: undeliverableItemsBody(undeliverableItems, undeliverableItemsCount),
      };
    }

    const bodyElements = [];

    streetDates.forEach((streetDate, index) => {
      const itemsWithStreetDate = groupedByStreetDate[streetDate];
      const date = streetDate ? `${format(new Date(streetDate), standardDatePattern)}` : '';
      const undeliverableItems = getUndeliverableItems(itemsWithStreetDate);
      const key = `streetDate_${index}`;
      bodyElements.push(
        <div key={key}>
          {multipleUndeliverableItemsBody(undeliverableItems, itemsWithStreetDate.length, date)}
        </div>,
      );
    });

    return {
      title: 'The items below cannot currently be ordered to your delivery address',
      body: (
        <>
          {bodyElements}
          {messageNextStepsText(undeliverableItemsCount)}
        </>
      ),
    };
  };

  const getTemporaryPreOrderErrorMessage = (temporaryPreOrderErrorItemsList = []) => {
    const temporaryPreOrderItemsCount = temporaryPreOrderErrorItemsList.length;
    const isMultipleUndeliverableItem = temporaryPreOrderItemsCount > 1;
    const isBasketOnlyTemporaryErrorItems = items.length === temporaryPreOrderItemsCount;
    const titleText = isMultipleUndeliverableItem ? 'these items' : 'this item';
    const plural = isMultipleUndeliverableItem ? 's' : '';
    const productsToDisplay = temporaryPreOrderErrorItemsList.map(item => (
      <li key={item.id}>{item.name}</li>
    ));

    return {
      title: `Sorry, there's a problem with ordering ${titleText}.`,
      body: (
        <>
          {
            !isBasketOnlyTemporaryErrorItems &&
            <ul>{productsToDisplay}</ul>
          }
          Please refresh the page and try again{getEditBasketOrRemoveItemLink(plural)} if the problem persists.
        </>
      ),
    };
  };

  const defaultUndeliverableItemsErrorMessage = (undeliverableItemsCount = 0) => {
    const plural = undeliverableItemsCount > 1 ? 's' : '';

    return {
      title: `Sorry, the item${plural} in your basket can't be delivered to this address`,
      body: (
        <>
          This may be due to courier restrictions, or the address is in an area we don’t deliver to. Please{' '}
          <UnderlinedButton onClick={onChangeAddressClick}>
            change your delivery address
          </UnderlinedButton> to continue.
        </>
      ),
    };
  };

  const defaultUndeliverableItemErrorMessage = (undeliverableItemsList = []) => {
    const undeliverableItems = getUndeliverableItems(undeliverableItemsList);
    const plural = undeliverableItemsList.length > 1 ? 's' : '';

    return {
      title: `The following item${plural} can't be delivered to this address:`,
      body: undeliverableItemsBody(undeliverableItems, undeliverableItemsList.length),
    };
  };

  if (undeliverableItemsInBasket?.length) {
    const {
      RESTRICTED_UNTIL_PRE_ORDER_STREET_DATE,
      TEMPORARY_PRE_ORDER_ERROR,
      UNDELIVERABLE,
    } = deliveryConstants;
    let itemTotal = 0;

    items.forEach((item) => {
      itemTotal += 1 + item.childItems?.length;
    });

    const undeliverableItemsList = undeliverableItemsInBasket
      .filter(item => item.undeliverableReasons?.includes(UNDELIVERABLE));
    const restrictedPreorderItemsList = undeliverableItemsInBasket
      .filter(item => item.undeliverableReasons &&
        item.undeliverableReasons.indexOf(RESTRICTED_UNTIL_PRE_ORDER_STREET_DATE) !== -1);
    const temporaryPreOrderErrorItemsList = undeliverableItemsInBasket
      .filter(item => item.undeliverableReasons?.includes(TEMPORARY_PRE_ORDER_ERROR));
    const otherPreOrderErrorItemsList = undeliverableItemsInBasket
      .filter(item => item.undeliverableReasons && (
        !item.undeliverableReasons.includes(UNDELIVERABLE) &&
        !(item.undeliverableReasons.indexOf(RESTRICTED_UNTIL_PRE_ORDER_STREET_DATE) !== -1) &&
        !item.undeliverableReasons.includes(TEMPORARY_PRE_ORDER_ERROR)
      ));

    const undeliverableItemsMessage = [];
    const undeliverablePreOrderItemsCount = undeliverableItemsList.length;
    const restrictedPreorderItemsCount = restrictedPreorderItemsList.length;
    const temporaryPreOrderItemsCount = temporaryPreOrderErrorItemsList.length;
    const otherPreOrderErrorItemsCount = otherPreOrderErrorItemsList.length;

    if (
      temporaryPreOrderItemsCount ||
      restrictedPreorderItemsCount ||
      undeliverablePreOrderItemsCount ||
      otherPreOrderErrorItemsCount
    ) {
      if (temporaryPreOrderItemsCount) {
        const message = getTemporaryPreOrderErrorMessage(temporaryPreOrderErrorItemsList);
        undeliverableItemsMessage.push(message);
      }

      if (restrictedPreorderItemsCount) {
        const message = getRestrictedPreOrderItemsErrorMessage(restrictedPreorderItemsList);
        undeliverableItemsMessage.push(message);
      }

      if (undeliverablePreOrderItemsCount) {
        if (undeliverablePreOrderItemsCount === itemTotal) {
          const message = defaultUndeliverableItemsErrorMessage(undeliverablePreOrderItemsCount);
          undeliverableItemsMessage.push(message);
        } else {
          const message = defaultUndeliverableItemErrorMessage(undeliverableItemsList);
          undeliverableItemsMessage.push(message);
        }
      }

      if (otherPreOrderErrorItemsCount) {
        if (otherPreOrderErrorItemsCount === itemTotal) {
          const message = defaultUndeliverableItemsErrorMessage(otherPreOrderErrorItemsCount);
          undeliverableItemsMessage.push(message);
        } else {
          const message = defaultUndeliverableItemErrorMessage(otherPreOrderErrorItemsList);
          undeliverableItemsMessage.push(message);
        }
      }
    } else if (undeliverableItemsInBasket.length === itemTotal) {
      const message = defaultUndeliverableItemsErrorMessage(undeliverableItemsInBasket.length);
      undeliverableItemsMessage.push(message);
    } else {
      const message = defaultUndeliverableItemErrorMessage(undeliverableItemsInBasket);
      undeliverableItemsMessage.push(message);
    }

    return undeliverableItemsMessage;
  }

  return undefined;
};

const UndeliverableErrorMessage = ({ undeliverableItemsMessage }) => (
  <Container marginBottom="3">
    <Message
      body={undeliverableItemsMessage.body}
      data-testid="delivery-address-undeliverable-items-in-basket-message"
      title={undeliverableItemsMessage.title}
      type="warning"
    />
  </Container>
);

UndeliverableErrorMessage.propTypes = {
  undeliverableItemsMessage: PropTypes.shape(),
};

UndeliverableErrorMessage.defaultProps = {
  undeliverableItemsMessage: '',
};

export default UndeliverableErrorMessage;
